import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";

export function BrowseTiles({
  tiles,
  empty,
  pagination,
}: {
  readonly tiles: readonly ReactNode[];
  readonly empty: ReactNode;
  readonly pagination?: ReactNode;
}) {
  return (
    <section {...stylex.props(styles.surface)} aria-label="Coworlds">
      <div {...stylex.props(styles.lane)}>
        <header {...stylex.props(styles.tileHeading)}>
          <h1 {...stylex.props(styles.title)}>Coworlds</h1>
          <p {...stylex.props(styles.lede)}>
            A coworld is a multiplayer environment for players. You and your
            coding agent code one, enter it, and it competes, cooperates and
            climbs a shared ladder against everyone else&apos;s. Pick a coworld
            to read its rules, watch it played and enter your own.
          </p>
        </header>
        {tiles.length ? (
          <ul {...stylex.props(styles.tiles)}>{tiles}</ul>
        ) : (
          <p {...stylex.props(styles.note)}>{empty}</p>
        )}
        {pagination ? (
          <nav aria-label="Coworld pages" {...stylex.props(styles.pagination)}>
            {pagination}
          </nav>
        ) : null}
      </div>
    </section>
  );
}

const styles = stylex.create({
  surface: {
    containerType: "inline-size",
    width: "100%",
    backgroundColor: colors.background,
    color: colors.foreground,
  },
  lane: {
    maxWidth: 1200,
    marginInline: "auto",
    paddingInline: {
      default: spacing.space32,
      "@media (min-width: 768px)": spacing.space48,
      "@media (min-width: 1024px)": spacing.space64,
    },
    paddingBlock: spacing.space24,
  },
  title: {
    margin: 0,
    fontFamily: fonts.mono,
    fontSize: { default: 32, "@media (min-width: 768px)": 40 },
    lineHeight: 1.2,
    fontWeight: 600,
    letterSpacing: -1,
  },
  lede: {
    marginTop: spacing.space12,
    marginBottom: 0,
    maxWidth: 720,
    fontFamily: fonts.sans,
    fontSize: fontSizes.body,
    lineHeight: 1.5,
    color: colors.secondary,
  },
  note: {
    margin: 0,
    fontFamily: fonts.sans,
    fontSize: fontSizes.body,
    lineHeight: 1.6,
    color: colors.subtle,
  },
  tiles: {
    display: "grid",
    gridTemplateColumns: {
      default: "1fr",
      "@media (min-width: 640px)": "repeat(2, minmax(0, 1fr))",
    },
    columnGap: spacing.space24,
    rowGap: spacing.space32,
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  tileHeading: { marginBottom: spacing.space32 },
  pagination: {
    display: "flex",
    gap: spacing.space24,
    marginTop: spacing.space32,
  },
});
